import { MessageSquareText, EyeOff, Eye } from 'lucide-react';
import { buildStarFillStates, safeReviewDisplayName } from '@dukanti/shared';
import type { AdminProductReviewRow } from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ReviewsTableProps {
  readonly rows: readonly AdminProductReviewRow[];
  readonly canModerate: boolean;
  readonly onRespond: (review: AdminProductReviewRow) => void;
  readonly onVisibility: (review: AdminProductReviewRow) => void;
}

function visibilityLabelKey(visibility: AdminProductReviewRow['visibility']): never {
  return `admin.reviews.visibility.${visibility}` as never;
}

function Stars({ rating }: Readonly<{ rating: number }>): React.JSX.Element {
  return (
    <span className="text-saffron inline-flex" dir="ltr" aria-hidden>
      {buildStarFillStates(rating).map((state, index) => (
        <span key={index}>{state === 'full' ? '★' : '☆'}</span>
      ))}
    </span>
  );
}

export function ReviewsTable({
  rows,
  canModerate,
  onRespond,
  onVisibility,
}: ReviewsTableProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const formatDate = (iso: string): string =>
    new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'short', day: 'numeric' }).format(
      new Date(iso),
    );

  return (
    <div
      dir={dir}
      data-testid="reviews-table"
      className="border-border bg-card shadow-admin-card hidden overflow-hidden rounded-lg border md:block"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.reviews.columns.product')}</TableHead>
            <TableHead>{t('admin.reviews.columns.rating')}</TableHead>
            <TableHead>{t('admin.reviews.columns.review')}</TableHead>
            <TableHead>{t('admin.reviews.columns.visibility')}</TableHead>
            <TableHead>{t('admin.reviews.columns.response')}</TableHead>
            <TableHead>{t('admin.reviews.columns.date')}</TableHead>
            <TableHead className="text-end">{t('admin.reviews.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} data-testid="reviews-table-row">
              <TableCell>
                <div className="min-w-0">
                  <p className="text-foreground truncate font-medium">
                    {row.product_name_snapshot}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.variant_option_summary_snapshot}
                  </p>
                </div>
              </TableCell>
              <TableCell>
                <Stars rating={row.rating} />
              </TableCell>
              <TableCell className="max-w-72">
                <p className="text-foreground line-clamp-2 break-words text-sm">
                  {row.review_text ?? t('reviews.verifiedPurchase')}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {safeReviewDisplayName(row.customer_display_name) ??
                    t('reviews.verifiedCustomer')}
                </p>
              </TableCell>
              <TableCell>
                <Badge variant={row.visibility === 'visible' ? 'success' : 'warning'}>
                  {t(visibilityLabelKey(row.visibility))}
                </Badge>
              </TableCell>
              <TableCell className="max-w-56">
                <p className="text-muted-foreground line-clamp-2 text-sm">
                  {row.merchant_response_text ?? t('reviews.noReviewsYet')}
                </p>
              </TableCell>
              <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                {formatDate(row.created_at)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canModerate}
                    title={!canModerate ? t('admin.reviews.actions.disabledForStaff') : undefined}
                    onClick={() => onRespond(row)}
                  >
                    <MessageSquareText className="size-4" aria-hidden />
                    {row.merchant_response_text
                      ? t('admin.reviews.actions.editResponse')
                      : t('admin.reviews.actions.respond')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canModerate}
                    title={!canModerate ? t('admin.reviews.actions.disabledForStaff') : undefined}
                    onClick={() => onVisibility(row)}
                  >
                    {row.visibility === 'visible' ? (
                      <EyeOff className="size-4" aria-hidden />
                    ) : (
                      <Eye className="size-4" aria-hidden />
                    )}
                    {row.visibility === 'visible'
                      ? t('admin.reviews.actions.hide')
                      : t('admin.reviews.actions.unhide')}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
